import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import type { OptionValue } from 'types/shared';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCurrentNetwork, useModal } from 'hooks';
import type { SettingsEntry } from '../types';
import { RuleEditAutocomplete } from './index';
import FilterValue from './FilterValue';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getReferencesOptions } from '../api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useFilterStore from '../hooks/useFilterStore';
import useRulePageMode from '../hooks/useRulePageMode';

interface IProps {
  groupIndex: number;
  index: number;
}

const getOptions = ({ name, id }: SettingsEntry) => ({ value: id, label: name });

const FilterForm: FC<IProps> = observer(({ index, groupIndex }) => {
  const { isRead } = useRulePageMode();
  const network = useCurrentNetwork();
  const { open: setIsHovered, close: setIsNotHovered, isOpen: isHovered } = useModal();
  const { data: references } = useSuspenseQuery(getReferencesOptions(network));
  const { onShowInfoAlert } = useEnqueueSnackbar();
  const filterStore = useFilterStore();

  const filter = filterStore.getFilter(groupIndex, index);

  const [filteredOperators, filteredValues] = useMemo(() => {
    const selectedFilterOptions = references.filters.find((f) => f.field.id === filter?.field);

    if (!selectedFilterOptions) return [[], []];

    return [selectedFilterOptions.operators.map(getOptions), selectedFilterOptions.values.map(getOptions)];
  }, [filter?.field, references.filters]);

  useEffect(() => {
    if (filter?.operator && !filteredOperators.find((fo) => fo.value === filter?.operator)) {
      filterStore.updateOperator(groupIndex, index, '');
    }
  }, [filter?.operator, filter?.value, filterStore, filteredOperators, filteredValues, groupIndex, index]);

  const handleChangeField = useCallback(
    (value: OptionValue) => {
      filterStore.updateField(groupIndex, index, value as string);
    },
    [groupIndex, index, filterStore]
  );

  const handleChangeOperator = useCallback(
    (value: OptionValue) => {
      filterStore.updateOperator(groupIndex, index, value as string);
    },
    [groupIndex, index, filterStore]
  );

  const handleRemoveFilter = () => {
    const result = filterStore.removeFilter(groupIndex, index);

    if (!result) {
      onShowInfoAlert(`You can't delete last filter in last filter group`);
    }
  };

  return (
    <Box
      id={`filter-${index}`}
      onMouseEnter={setIsHovered}
      onMouseLeave={setIsNotHovered}
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <Box
        sx={(t) => ({
          width: 'calc(100% - 50px)',
          border: `1px solid ${t.palette.grey[200]}`,
          borderRadius: t.borderRadius.md,
          transition: '1s ease-in-out',
        })}
      >
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            color: t.palette.primary.main,
            backgroundColor: 'rgba(41, 121, 255, 0.04)',
            px: 1,
            py: 0.75,
            fontWeight: 500,
          })}
        >
          <Box sx={{ display: 'flex', mr: 4 }}>
            <FilterListIcon fontSize="large" sx={{ fontSize: 24, mr: 1 }} />{' '}
            <Typography variant="subtitle2">Filter {index + 1}</Typography>
          </Box>
          {network === 'facebook' && (filter?.operator === 'IN' || filter?.operator === 'NOT_IN') && (
            <Box sx={{ display: 'flex' }}>
              <InfoOutlinedIcon fontSize="small" sx={{ fontSize: 16, mr: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                Values in filter with “IN” operator have “OR” relation
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
          <Box
            sx={{
              flex: 1,
            }}
          >
            <RuleEditAutocomplete
              sx={{ height: '100%' }}
              allSelectable={false}
              value={filter?.field}
              InputProps={{
                placeholder: 'Field',
                disabled: isRead,
              }}
              onChange={handleChangeField}
              id="field"
              options={references.filters.map((f) => getOptions({ id: f.field.id, name: f.field.name }))}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              border: '1px solid rgba(0, 0, 0, 0.15)',
              borderBottomColor: 'transparent',
              borderTopColor: 'transparent',
            }}
          >
            <RuleEditAutocomplete
              allSelectable={false}
              value={filter?.operator}
              InputProps={{
                placeholder: 'Operator',
                disabled: isRead,
              }}
              onChange={handleChangeOperator}
              id="operator"
              options={filteredOperators}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <FilterValue groupIndex={groupIndex} index={index} values={filteredValues} />
          </Box>
        </Box>
      </Box>
      {!isRead && (
        <Box sx={{ width: 50 }}>
          {isHovered && (
            <Tooltip title="Delete filter" placement="top" arrow>
              <IconButton onClick={handleRemoveFilter}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
});

export default FilterForm;
